import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Video, Phone, MessageSquare, Mic, MicOff, VideoOff, PhoneOff, Send, Settings, Maximize2, Minimize2, FileText, ClipboardList } from 'lucide-react';

const DoctorConsultation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const { appointment } = location.state || {};
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: user?.name,
      text: 'Hello! How are you feeling today?',
      timestamp: new Date(Date.now() - 120000),
      isOwn: true
    },
    {
      id: 2,
      sender: appointment?.patient,
      text: 'Hi Doctor, I\'m doing okay. I have some questions about my condition.',
      timestamp: new Date(Date.now() - 60000),
      isOwn: false
    }
  ]);
  const [messageText, setMessageText] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [duration, setDuration] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    // Simulate connection
    setTimeout(() => {
      setConnectionStatus('connected');
    }, 2000);

    // Start timer
    const timer = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleEndCall = () => {
    if (window.confirm('Are you sure you want to end this consultation?')) {
      navigate('/doctor/appointments');
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (messageText.trim()) {
      setMessages([...messages, {
        id: Date.now(),
        sender: user?.name,
        text: messageText,
        timestamp: new Date(),
        isOwn: true
      }]);
      setMessageText('');
      
      // Simulate patient response
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: Date.now(),
          sender: appointment?.patient,
          text: 'Thank you, doctor. I understand.',
          timestamp: new Date(),
          isOwn: false
        }]);
      }, 2000);
    }
  };

  if (!appointment) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Consultation Not Found</h2>
          <p className="text-gray-600 mb-6">The consultation session you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/doctor/appointments')}
            className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700"
          >
            Back to Appointments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 md:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {appointment.mode === 'video' && <Video className="w-5 h-5 text-emerald-400" />}
              {appointment.mode === 'audio' && <Phone className="w-5 h-5 text-emerald-400" />}
              {appointment.mode === 'chat' && <MessageSquare className="w-5 h-5 text-emerald-400" />}
              <div>
                <h1 className="text-white font-semibold">{appointment.patient}</h1>
                <p className="text-sm text-gray-400">{appointment.type}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-emerald-500/20 rounded-full">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-emerald-400 font-medium">
                {connectionStatus === 'connecting' ? 'Connecting...' : 'Connected'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowNotes(!showNotes)}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-white text-sm flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Notes
            </button>
            <span className="text-white font-mono">{formatDuration(duration)}</span>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-white"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Video/Audio Area */}
        {(appointment.mode === 'video' || appointment.mode === 'audio') && (
          <div className="flex-1 relative bg-black flex items-center justify-center">
            {appointment.mode === 'video' ? (
              <div className="relative w-full h-full">
                {/* Patient Video (simulated) */}
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                  <div className="text-center">
                    <div className="w-32 h-32 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <span className="text-5xl font-bold text-white">{appointment.patient.charAt(4)}</span>
                    </div>
                    <p className="text-white text-xl font-semibold">{appointment.patient}</p>
                    <p className="text-gray-400">Patient's camera is on</p>
                  </div>
                </div>
                
                {/* Self Video (Picture-in-Picture) */}
                <div className="absolute bottom-4 right-4 w-48 h-36 bg-gray-800 rounded-xl overflow-hidden border-2 border-emerald-600">
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-800">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center mb-2 mx-auto">
                        <span className="text-2xl font-bold text-white">{user?.name?.charAt(0)}</span>
                      </div>
                      <p className="text-white text-sm">You (Doctor)</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-48 h-48 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Phone className="w-24 h-24 text-white" />
                </div>
                <p className="text-white text-2xl font-semibold mb-2">Audio Call in Progress</p>
                <p className="text-gray-400 mb-2">{appointment.patient}</p>
                <p className="text-gray-500 text-sm">{isMuted ? 'Microphone is muted' : 'Microphone is active'}</p>
              </div>
            )}
          </div>
        )}

        {/* Sidebar - Chat and Notes */}
        <div className={`${appointment.mode === 'chat' ? 'flex-1' : 'w-full md:w-96'} bg-gray-800 flex flex-col border-l border-gray-700`}>
          {/* Tab Headers */}
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => setShowNotes(false)}
              className={`flex-1 p-4 font-semibold transition-colors ${
                !showNotes 
                  ? 'text-emerald-400 border-b-2 border-emerald-400 bg-gray-750' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Chat
              </div>
            </button>
            <button
              onClick={() => setShowNotes(true)}
              className={`flex-1 p-4 font-semibold transition-colors ${
                showNotes 
                  ? 'text-emerald-400 border-b-2 border-emerald-400 bg-gray-750' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Notes
              </div>
            </button>
          </div>

          {/* Chat Section */}
          {!showNotes && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div key={msg.id} className={`flex ${msg.isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-xl p-3 ${
                        msg.isOwn 
                          ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white' 
                          : 'bg-gray-700 text-white'
                      }`}>
                        <p className="text-xs font-semibold mb-1 opacity-80">
                          {msg.isOwn ? 'You' : msg.sender}
                        </p>
                        <p className="text-sm">{msg.text}</p>
                        <span className="text-xs opacity-70 mt-1 block">
                          {msg.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    type="submit"
                    className="p-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:from-emerald-700 hover:to-green-700 transition-all"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Notes Section */}
          {showNotes && (
            <div className="flex-1 flex flex-col p-4">
              <div className="mb-4">
                <h3 className="text-white font-semibold mb-2">Consultation Notes</h3>
                <p className="text-gray-400 text-sm">Document important details during the consultation</p>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Patient symptoms, diagnosis, prescription, recommendations..."
                className="flex-1 bg-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              />
              <button className="mt-4 px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl hover:from-emerald-700 hover:to-green-700 transition-all font-semibold">
                Save Notes
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Controls Footer */}
      <div className="bg-gray-800 border-t border-gray-700 px-4 md:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-4">
          {(appointment.mode === 'video' || appointment.mode === 'audio') && (
            <>
              <button
                onClick={() => setIsMuted(!isMuted)}
                className={`p-4 rounded-full transition-all ${
                  isMuted 
                    ? 'bg-red-500 hover:bg-red-600' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
              >
                {isMuted ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
              </button>
              
              {appointment.mode === 'video' && (
                <button
                  onClick={() => setIsVideoOff(!isVideoOff)}
                  className={`p-4 rounded-full transition-all ${
                    isVideoOff 
                      ? 'bg-red-500 hover:bg-red-600' 
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  {isVideoOff ? <VideoOff className="w-6 h-6 text-white" /> : <Video className="w-6 h-6 text-white" />}
                </button>
              )}
            </>
          )}
          
          <button
            onClick={handleEndCall}
            className="px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-full transition-all flex items-center gap-2 text-white font-semibold shadow-lg"
          >
            <PhoneOff className="w-6 h-6" />
            End Consultation
          </button>
          
          <button className="p-4 bg-gray-700 hover:bg-gray-600 rounded-full transition-all">
            <Settings className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorConsultation;
